/**
 * subcontractReportData.js
 *
 * Pure data-shaping layer for the Subcontract Report. Follows
 * contractFinancials.js's own stated design constraint: pure functions,
 * no Supabase import, no React state — all data fetching remains the
 * caller's responsibility (SubcontractReportControls.jsx /
 * SubcontractReport.jsx).
 *
 * This module does NOT compute retention/certified/paid/outstanding
 * itself — it calls the existing computeContractFinancials() (the
 * single financial SSOT for this domain, unchanged) and only adds the
 * Variation Chain layer (Original → Variations → Revised), which
 * contractFinancials.js does not currently cover.
 */

import { computeContractFinancials } from './contractFinancials'
import { supabase } from './supabase'

/**
 * Groups subcontract_addendums by contract_id, sorted by addendum_no.
 *
 * @param {Array<{contract_id, addendum_no, description, value_change,
 *   new_contract_value, addendum_date}>} addendums
 * @returns {Map<string|number, Array>} contractId -> sorted addendums
 */
function groupAddendumsByContract(addendums) {
  const map = new Map()
  for (const a of (addendums || [])) {
    if (!map.has(a.contract_id)) map.set(a.contract_id, [])
    map.get(a.contract_id).push(a)
  }
  for (const list of map.values()) {
    list.sort((a, b) => (a.addendum_no || 0) - (b.addendum_no || 0))
  }
  return map
}

/**
 * Builds the Variation Chain for one contract: Original Contract Value
 * (derived, not stored anywhere directly) → each addendum in order →
 * Revised Contract Value.
 *
 * Original Value derivation: subcontract_addendums.new_contract_value
 * is the cumulative value AFTER that addendum, not the value change
 * alone (confirmed against Subcontracts.jsx's addendum-creation logic:
 * `newValue = contract.contract_value + valueChange` at insert time).
 * So Original = the FIRST addendum's new_contract_value minus that same
 * addendum's value_change — i.e. what the contract was worth
 * immediately before addendum #1. If there are no addendums at all,
 * Original = the contract's current value (no variation history
 * exists).
 *
 * @param {{id, contract_value}} contract
 * @param {Array} contractAddendums - already sorted by addendum_no
 * @returns {{originalValue, variations: Array, totalVariations,
 *   revisedValueFromChain, revisedValueActual, reconciles: boolean}}
 */
function buildVariationChain(contract, contractAddendums) {
  const currentValue = parseFloat(contract.contract_value) || 0

  if (!contractAddendums || contractAddendums.length === 0) {
    return {
      originalValue: currentValue,
      variations: [],
      totalVariations: 0,
      revisedValueFromChain: currentValue,
      revisedValueActual: currentValue,
      reconciles: true,
    }
  }

  const first = contractAddendums[0]
  const originalValue = (parseFloat(first.new_contract_value) || 0) - (parseFloat(first.value_change) || 0)

  const variations = contractAddendums.map(a => ({
    addendumNo: a.addendum_no,
    date: a.addendum_date,
    description: a.description,
    valueChange: parseFloat(a.value_change) || 0,
    newContractValue: parseFloat(a.new_contract_value) || 0,
  }))

  const totalVariations = variations.reduce((s, v) => s + v.valueChange, 0)
  const revisedValueFromChain = originalValue + totalVariations

  // Reconciliation check (mandatory per the approved spec): the chain's
  // own arithmetic (Original + ΣVariations) must equal the contract's
  // CURRENT stored value. A mismatch is a REAL possible state — e.g. an
  // addendum was recorded but its linked PO has not yet been
  // re-approved (Subcontracts.jsx's own documented behavior: "the
  // contract keeps its current value... until the PO re-approval flows
  // through"). This is surfaced explicitly, never silently reconciled
  // by preferring one number over the other.
  const reconciles = Math.abs(revisedValueFromChain - currentValue) < 0.01

  return {
    originalValue,
    variations,
    totalVariations,
    revisedValueFromChain,
    revisedValueActual: currentValue,
    reconciles,
  }
}

/**
 * Builds the complete report dataset for a set of contracts already
 * filtered by the caller (contract type, contractor, status). Combines:
 *   - computeContractFinancials() — certified/retention/paid/outstanding
 *     (unchanged SSOT, called exactly as Subcontracts.jsx/
 *     SubcontractReport.jsx already do)
 *   - buildVariationChain() — the one genuinely new piece of logic in
 *     this report, since contractFinancials.js does not cover
 *     variations at all
 *
 * @param {Array} contracts - already filtered (type/contractor/status)
 * @param {Array} ipcs
 * @param {Array} retentionReleases
 * @param {Array} paymentEntries
 * @param {Array} addendums
 * @returns {Array} one row per contract, full financial chain
 */
export function buildSubcontractReportRows(contracts, ipcs, retentionReleases, paymentEntries, addendums) {
  const finRows = computeContractFinancials(contracts, ipcs, retentionReleases, paymentEntries)
  const finById = new Map(finRows.map(r => [r.id, r]))
  const addendumsByContract = groupAddendumsByContract(addendums)

  return contracts.map(c => {
    const fin = finById.get(c.id) || {
      cumulative_certified: 0, cumulative_net_payable: 0,
      retention_total: 0, retention_released: 0, retention_held: 0,
      total_paid: 0, outstanding: 0,
    }
    const chain = buildVariationChain(c, addendumsByContract.get(c.id))

    return {
      id: c.id,
      contract_type: c.contract_type,
      contract_no: c.contract_no || '',
      contractor_name: c.contractor_name || '',
      status: c.status || 'draft',
      contract_date: c.contract_date || null,
      practical_completion_date: c.practical_completion_date || null,
      dlp_months: c.dlp_months ?? null,

      original_value: chain.originalValue,
      variations: chain.variations,
      total_variations: chain.totalVariations,
      revised_value: chain.revisedValueActual, // the actual, current, authoritative value
      variation_chain_reconciles: chain.reconciles,
      variation_chain_expected_revised: chain.revisedValueFromChain, // shown only if reconciles === false

      certified: fin.cumulative_certified,
      net_payable: fin.cumulative_net_payable,
      retention_gross: fin.retention_total,
      retention_released: fin.retention_released,
      retention_held: fin.retention_held,
      paid: fin.total_paid,
      outstanding: fin.outstanding,
      remaining_commitment: chain.revisedValueActual - fin.cumulative_certified,
    }
  })
}

/**
 * Aggregates report rows into Executive Summary KPIs — every figure
 * here is a straight sum of already-computed per-contract values from
 * buildSubcontractReportRows(), never a new calculation.
 */
export function buildExecutiveSummary(rows) {
  const sum = (key) => rows.reduce((s, r) => s + (r[key] || 0), 0)
  return {
    contractCount: rows.length,
    totalRevisedValue: sum('revised_value'),
    totalVariations: sum('total_variations'),
    totalCertified: sum('certified'),
    totalPaid: sum('paid'),
    totalRetentionGross: sum('retention_gross'),
    totalRetentionReleased: sum('retention_released'),
    totalRetentionHeld: sum('retention_held'),
    totalOutstanding: sum('outstanding'),
    totalRemainingCommitment: sum('remaining_commitment'),
  }
}

/**
 * buildIpcStatementRows() — single shared source for the IPC-by-IPC
 * statement data, consumed by BOTH Subcontracts.jsx's on-screen
 * "Statement of Account" modal (openStatement()) and
 * financialStatementPdfRenderer.js's printed IPC Statement section.
 *
 * This is openStatement()'s own query/aggregation logic moved here
 * verbatim (same tables, same columns, same status filter
 * ['approved','paid'], same ipc_number ordering, same cumulative-sum
 * formulas) — not a re-derivation. The last row's cumulative_net is
 * structurally guaranteed to equal
 * computeContractFinancials().cumulative_net_payable for the same
 * contract, because both read net_payable from the identical
 * ipc_certificates population filtered by the identical status list —
 * see the audit that preceded this change for the field-by-field proof.
 *
 * contract_value is deliberately NOT included in this function's
 * output — it is a property of the contract row itself, not a
 * derived IPC figure, and callers already have it from their own
 * contract fetch (row.contract_value / rows[].contract_value).
 *
 * @param {string} contractId
 * @param {'material'|'equipment'} contractType
 * @param {string|null} poId - pass null/undefined to trigger the same
 *   fallback lookup openStatement() performed when row.po_id was absent
 * @returns {Promise<{rows: Array, unlinkedTotal: number, totalPaidLinked: number}>}
 */
export async function buildIpcStatementRows(contractId, contractType, poId) {
  const idKey = contractType === 'equipment' ? 'equipment_contract_id' : 'subcontract_id'
  const { data: ipcs } = await supabase
    .from('ipc_certificates')
    .select('id, ipc_number, gross_amount, retention_amount, net_payable, status, approval_stage, submission_date, approved_date')
    .eq(idKey, contractId)
    .in('status', ['approved', 'paid'])
    .order('ipc_number')

  let resolvedPoId = poId
  if (!resolvedPoId) {
    const table = contractType === 'equipment' ? 'equipment_contracts' : 'subcontracts'
    const { data: cRow } = await supabase.from(table).select('po_id').eq('id', contractId).maybeSingle()
    resolvedPoId = cRow?.po_id || null
  }
  const { data: allPayments } = await supabase
    .from('cf_payment_entries')
    .select('id, ipc_id, amount_inc_vat, date')
    .eq('po_id', resolvedPoId || '__none__')

  const paidByIpc = {}
  let unlinkedTotal = 0
  ;(allPayments || []).forEach(p => {
    if (p.ipc_id) paidByIpc[p.ipc_id] = (paidByIpc[p.ipc_id] || 0) + (parseFloat(p.amount_inc_vat) || 0)
    else unlinkedTotal += (parseFloat(p.amount_inc_vat) || 0)
  })
  const totalPaidLinked = Object.values(paidByIpc).reduce((s, v) => s + v, 0)

  let cumGross = 0, cumRet = 0, cumNet = 0, cumPaidLinked = 0
  const rows = (ipcs || []).map(ipc => {
    cumGross += parseFloat(ipc.gross_amount) || 0
    cumRet   += parseFloat(ipc.retention_amount) || 0
    cumNet   += parseFloat(ipc.net_payable) || 0
    const paidThis = paidByIpc[ipc.id] || 0
    cumPaidLinked += paidThis
    return {
      ipc_number: ipc.ipc_number,
      gross: parseFloat(ipc.gross_amount) || 0,
      retention: parseFloat(ipc.retention_amount) || 0,
      net_payable: parseFloat(ipc.net_payable) || 0,
      cumulative_net: cumNet,
      cumulative_retention: cumRet,
      paid_this_ipc: paidThis,
      cumulative_paid_linked: cumPaidLinked,
      balance_linked: cumNet - cumPaidLinked,
      submission_date: ipc.submission_date,
      approved_date: ipc.approved_date,
    }
  })

  return { rows, unlinkedTotal, totalPaidLinked }
}
